/**
 * @file Icon.jsx
 * @location src/components/ui/Icon.jsx
 * @role Lucide React 아이콘 래퍼 — 디자인 시스템 일관성
 *
 *  사용: <Icon name="search" size={16} />
 *  사이즈 기본: 16. stroke-width 기본: 1.75 (Lucide 기본 2 보다 살짝 가늘게)
 *
 *  이름 매핑은 의도적으로 도메인 어휘를 사용해 사용처에서 의미가 명확해짐.
 *  필요한 아이콘만 import 해 트리 셰이킹.
 */
import { memo } from 'react';
import { warn } from '../../utils/debug.js';
import {
  // 네비/툴
  Search, Settings, Bell, Trash2, Sun, Moon,
  // 액션
  Check, X, Plus, Minus, ChevronLeft, ChevronRight, ChevronDown, ArrowDown,
  RotateCcw, Undo2, Download, Upload, Edit3,
  Send, Inbox, MessageCircle, Paperclip, Link,
  // 도메인
  Calendar, CalendarDays, Clock, Folder, Target, Users,
  UserCog, Building2, BarChart3, TrendingUp, FileText,
  Coffee, Palmtree,
  AlertOctagon, AlertCircle, AlertTriangle, Info, Hourglass, Lock,
  ListTodo, Sparkles, Star, ThumbsUp,
  Mail, MailOpen, MessageSquare, Layout, Pin, MoreHorizontal,
  LogOut, User,
} from 'lucide-react';

const ICONS = {
  // ── 네비·툴 ──
  search: Search, settings: Settings, bell: Bell, trash: Trash2,
  sun: Sun, moon: Moon,
  // ── 액션 ──
  check: Check, x: X, plus: Plus, minus: Minus,
  'chevron-left': ChevronLeft, 'chevron-right': ChevronRight,
  'chevron-down':  ChevronDown,
  'arrow-down': ArrowDown,
  reset: RotateCcw, undo: Undo2,
  download: Download, upload: Upload,
  edit: Edit3, send: Send, inbox: Inbox,
  message: MessageCircle, attach: Paperclip, link: Link,
  // ── 도메인 ──
  calendar: Calendar, 'calendar-days': CalendarDays, clock: Clock, pin: Pin,
  folder: Folder, goal: Target, team: Users,
  manager: UserCog, hr: Building2,
  chart: BarChart3, trend: TrendingUp, document: FileText,
  evening: Coffee, leave: Palmtree,
  urgent: AlertOctagon, warn: AlertCircle, alert: AlertTriangle, info: Info,
  pending: Hourglass, lock: Lock,
  todo: ListTodo, sparkles: Sparkles, star: Star, like: ThumbsUp,
  mail: Mail, 'mail-open': MailOpen, chat: MessageSquare, board: Layout,
  more: MoreHorizontal, logout: LogOut, user: User,
};

// Icon 은 모든 곳에서 빈번하게 쓰임 — memo 로 리스트 안 리렌더 차단.
export const Icon = memo(function Icon({ name, size = 16, strokeWidth = 1.75, className = '', style, ...rest }) {
  const Cmp = ICONS[name];
  if (!Cmp) {
    // 개발 중 실수 잡기 — DEV 콘솔에만 경고 (prod 무음)
    warn(`<Icon name="${name}" /> not registered`);
    return null;
  }
  return (
    <Cmp
      size={size}
      strokeWidth={strokeWidth}
      className={`icon ${className}`.trim()}
      style={{ flexShrink: 0, ...style }}
      aria-hidden="true"
      {...rest}
    />
  );
});
